import {
  MetricDataTypeDef,
  MetricDefinition,
  MetricDimensionDef,
  MetricsExplorerSchema,
} from "@/lib/types/metricsExplorer.ts";

/** Dimensions every table carries; not useful to group by, or internal to the store. */
const HIDDEN_DIMENSIONS = ["check_id", "check_date", "check_time"];

export const findCheckType = (schema: MetricsExplorerSchema | undefined, checkType: string): MetricDataTypeDef[] => {
  const entry = schema?.check_types.find((item) => item.check_type === checkType);
  return entry?.data_types ?? [];
};

/**
 * Data types queryable for every one of the selected check types.
 *
 * An intersection rather than a union: a series is one query against one table, so a data
 * type only half the selected checks write to would silently return partial data.
 */
export const availableDataTypes = (
  schema: MetricsExplorerSchema | undefined,
  checkTypes: string[]
): MetricDataTypeDef[] => {
  if (!schema || checkTypes.length === 0) {
    return [];
  }
  const [first, ...rest] = checkTypes;
  const candidates = findCheckType(schema, first);
  return candidates.filter((dataType) =>
    rest.every((checkType) => findCheckType(schema, checkType).some((other) => other.name === dataType.name))
  );
};

export const findDataType = (
  schema: MetricsExplorerSchema | undefined,
  checkTypes: string[],
  dataTypeName: string
): MetricDataTypeDef | undefined =>
  availableDataTypes(schema, checkTypes).find((dataType) => dataType.name === dataTypeName);

/** Only numeric metrics can be aggregated, and the explorer only draws aggregates. */
export const availableMetrics = (dataType: MetricDataTypeDef | undefined): MetricDefinition[] =>
  (dataType?.metrics ?? []).filter((metric) => metric.numeric);

export const availableDimensions = (dataType: MetricDataTypeDef | undefined): MetricDimensionDef[] =>
  (dataType?.dimensions ?? []).filter((dimension) => !HIDDEN_DIMENSIONS.includes(dimension.name));

/**
 * Narrow a series' selections to what the current data type still offers.
 *
 * Called when the checks or data type change: metrics, group-by dimensions and filters
 * chosen for the previous table would otherwise linger and produce a request the backend
 * rejects.
 */
export const pruneSelections = (
  dataType: MetricDataTypeDef | undefined,
  metrics: string[],
  groupBy: string[],
  filters: Record<string, string[]>
): {metrics: string[]; groupBy: string[]; filters: Record<string, string[]>} => {
  const metricNames = new Set(availableMetrics(dataType).map((metric) => metric.name));
  const dimensionNames = new Set(availableDimensions(dataType).map((dimension) => dimension.name));
  return {
    metrics: metrics.filter((metric) => metricNames.has(metric)),
    groupBy: groupBy.filter((dimension) => dimensionNames.has(dimension)),
    filters: Object.fromEntries(Object.entries(filters).filter(([dimension]) => dimensionNames.has(dimension))),
  };
};

export const uniqueCheckTypes = (types: string[]): string[] => Array.from(new Set(types)).sort();
